import React from 'react';

import PropTypes from 'prop-types';

import { formatCurrency } from '@atc/string-fns';

import { InfoIcon } from 'reaxl-molecules';

/**
 *  Breaks pricing data into a user friendly data table.
 *
 */
function PricingBreakdown({
    data: {
        dealerDiscountedPrice,
        incentive,
        msrp,
        reductions = [],
        salePrice,
        discounts = [],
        isMismatch,
    } = {},
    showDealerAdjustment,
    ...rest
}) {
    if (!msrp && !salePrice && !incentive) {
        return <div className="text-size-500">Call Dealer For Price</div>;
    }

    const renderTooltip = (tooltip) => (
        <InfoIcon
            infoText={tooltip}
            className="margin-left-1"
        />
    );

    const renderPriceRow = ({ label, value, priceClassName, tooltip, ...rowRest }) => value && (
        <div
            {...rowRest}
            className="display-flex justify-content-between align-items-center padding-vertical-1"
        >
            <div className={priceClassName}>
                {label}
                {tooltip && renderTooltip(tooltip)}
            </div>
            <div className={priceClassName}>{value}</div>
        </div>
    );

    const priceMismatch = !!isMismatch;
    const dealerAskingPrice = dealerDiscountedPrice || salePrice;
    const showDealerAskingPrice = !!dealerAskingPrice && !priceMismatch && (!incentive || dealerAskingPrice !== incentive);

    /* for visibility of dealer additions that raise price above msrp */
    const dealerAdjustment = incentive > msrp && showDealerAdjustment;
    const totalDiscounts = discounts && discounts.reduce((sum, currentValue) => sum + currentValue.value, 0);
    const totalReductions = reductions && reductions.reduce((sum, currentValue) => sum + currentValue.value, 0);
    /* incentive price already includes discounts & reductions = removal for accurate adjust */
    const dealerAdjustmentValue = incentive - msrp - (!!discounts && -totalDiscounts) - (!!reductions && -totalReductions);
    const enableDealerAdjustment = dealerAdjustment && dealerAdjustmentValue > 0;

    return (
        <div
            data-cmp="pricingBreakdown"
            {...rest}
        >
            {msrp && renderPriceRow({
                label: 'MSRP',
                value: formatCurrency(msrp),
            })}

            {reductions && reductions.map((reduction, index) => reduction.label.startsWith('Dealer') && renderPriceRow({
                key: reduction.label + reduction.value,
                label: reduction.label,
                value: `${reduction.label.includes('Add-on') ? '+' : '-'}${formatCurrency(reduction.value)}`,
                tooltip: reduction.tooltip,
            }))}

            {msrp && (showDealerAskingPrice || enableDealerAdjustment) && <hr className="margin-vertical-2" />}

            {showDealerAskingPrice && renderPriceRow({
                label: 'Dealer Asking Price',
                value: formatCurrency(dealerAskingPrice),
                priceClassName: 'text-bold',
            })}

            {discounts && discounts.map((discount, index) => renderPriceRow({
                key: discount.label + discount.value,
                label: `${discount.label} ${discount.expiration ? `(expires ${discount.expiration})` : ''}`,
                value: `-${formatCurrency(discount.value)}`,
                tooltip: discount.tooltip,
            }))}

            {reductions && reductions.map((reduction, index) => (reduction.label.startsWith('Special') || reduction.label.startsWith('Applied')) && renderPriceRow({
                key: reduction.label + reduction.value,
                label: reduction.label,
                value: `${reduction.label.includes('Add-on') ? '+' : '-'}${formatCurrency(reduction.value)}`,
                tooltip: reduction.tooltip,
            }))}

            {enableDealerAdjustment && renderPriceRow({
                label: 'Dealer Adjustment',
                value: formatCurrency(dealerAdjustmentValue),
            })}

            {incentive && (showDealerAskingPrice || (msrp && !showDealerAskingPrice)) && <hr className="margin-vertical-2" />}

            {incentive && renderPriceRow({
                label: 'Listing Price',
                value: formatCurrency(incentive),
                priceClassName: 'text-size-500 text-bold',
            })}
        </div>
    );
}

PricingBreakdown.propTypes /* remove-proptypes */ = {
    /*
    *   A standard axl price data object with msrp and salePrice and an array of discount objects
    */
    data: PropTypes.shape({
        dealerDiscountedPrice: PropTypes.number,
        incentive: PropTypes.number,
        msrp: PropTypes.number,
        salePrice: PropTypes.number,
        reductions: PropTypes.array,
        discounts: PropTypes.array,
        isMismatch: PropTypes.bool,
    }),
};

export default PricingBreakdown;
